import gsap from 'gsap'
import { defaultParams } from '../config/defaults.js'
import { params, saveParams } from '../config/persistence.js'
import { builtinPresets } from '../config/presets.js'
import { isMobile } from '../core/viewport.js'

export const isDebug = new URLSearchParams(window.location.search).get('debug') === 'true'

export const createOverlay = () => {
    if (isDebug) return

    // --- Bottom vignette ---
    const vignette = document.createElement('div')
    vignette.className = 'bottom-vignette'
    document.body.appendChild(vignette)

    // --- Container ---
    const container = document.createElement('div')
    container.className = 'bottom-ui'
    document.body.appendChild(container)

    // --- Header row (brand + rotate toggle) ---
    const header = document.createElement('div')
    header.className = 'bottom-header'
    container.appendChild(header)

    const brandLeft = document.createElement('div')
    brandLeft.className = 'brand-left'
    header.appendChild(brandLeft)

    const topBrand = document.createElement('a')
    topBrand.className = 'brand-author'
    topBrand.href = 'https://niccolofanton.dev'
    topBrand.target = '_blank'
    topBrand.rel = 'noopener'
    topBrand.textContent = 'niccolofanton.dev'
    brandLeft.appendChild(topBrand)

    const brand = document.createElement('span')
    brand.className = 'brand-main'
    brand.textContent = 'particles'
    brandLeft.appendChild(brand)

    // --- Info button ---
    const rightButtons = document.createElement('div')
    rightButtons.style.cssText = 'display:flex;gap:4px;align-items:center'
    header.appendChild(rightButtons)

    const infoBtn = document.createElement('button')
    infoBtn.className = 'info-btn'
    infoBtn.textContent = 'i'
    infoBtn.addEventListener('click', () => toggleInfoPopup(true))
    rightButtons.appendChild(infoBtn)

    const rotateToggle = document.createElement('button')
    rotateToggle.className = 'rotate-toggle'
    rotateToggle.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6"/><path d="M21.34 13.05A10 10 0 1 1 18.57 4.43L21.5 2"/></svg>`
    rotateToggle.title = 'Auto-rotate'
    if (params.autoRotate) rotateToggle.classList.add('active')
    rotateToggle.addEventListener('click', () => {
        params.autoRotate = !params.autoRotate
        rotateToggle.classList.toggle('active', params.autoRotate)
        saveParams()
        window.dispatchEvent(new CustomEvent('toggle-rotate', { detail: params.autoRotate }))
    })
    rightButtons.appendChild(rotateToggle)

    // --- Info popup ---
    const overlay = document.createElement('div')
    overlay.className = 'info-overlay'
    const interactionRows = isMobile
        ? `<div class="info-row"><span class="info-key">Touch + drag</span><span class="info-val">Attract particles toward finger</span></div>
           <div class="info-row"><span class="info-key">Two fingers</span><span class="info-val">Push particles with wind</span></div>
           <div class="info-row"><span class="info-key">Pinch</span><span class="info-val">Zoom in / out</span></div>`
        : `<div class="info-row"><span class="info-key">Move mouse</span><span class="info-val">Push particles with wind</span></div>
           <div class="info-row"><span class="info-key">Click + drag</span><span class="info-val">Attract particles toward cursor</span></div>
           <div class="info-row"><span class="info-key">Scroll</span><span class="info-val">Zoom in / out</span></div>
           <div class="info-row"><span class="info-key">Right-drag</span><span class="info-val">Rotate the camera</span></div>`

    overlay.innerHTML = `
        <div class="info-overlay-bg"></div>
        <div class="info-popup">
            <h2>Particles</h2>
            <p>A real-time GPU particle system built with <strong>Three.js</strong> and <strong>GPGPU shaders</strong>. Hundreds of thousands of particles are simulated entirely on the GPU using flow fields, turbulence, and velocity dynamics.</p>
            <p>Switch between presets to explore different shapes, colors, and physics — from bioluminescent spheres to pointillist paintings.</p>
            <div class="info-section">
                <h3>Interaction</h3>
                ${interactionRows}
                <div class="info-row"><span class="info-key">?debug=true</span><span class="info-val">Open the full control panel</span></div>
            </div>
            <div class="info-section" style="border:none;padding-top:8px">
                <p style="font-size:11px;color:rgba(255,255,255,0.2)">An experiment by Niccol\u00f2 Fanton</p>
            </div>
        </div>
    `
    document.body.appendChild(overlay)
    overlay.querySelector('.info-overlay-bg').addEventListener('click', () => toggleInfoPopup(false))
    overlay.querySelector('.info-popup').addEventListener('click', (e) => e.stopPropagation())

    // --- Preset bar ---
    const barWrapper = document.createElement('div')
    barWrapper.className = 'preset-bar-wrapper'
    container.appendChild(barWrapper)

    const bar = document.createElement('div')
    bar.className = 'preset-bar'
    barWrapper.appendChild(bar)

    // Update fade edges on scroll
    const updateFadeEdges = () => {
        const sl = bar.scrollLeft
        const maxScroll = bar.scrollWidth - bar.clientWidth
        barWrapper.classList.toggle('fade-left', sl > 4)
        barWrapper.classList.toggle('fade-right', sl < maxScroll - 4)
    }
    bar.addEventListener('scroll', updateFadeEdges, { passive: true })
    requestAnimationFrame(() => {
        updateFadeEdges()
        // Subtle scroll hint on mobile — nudge right and back
        if (bar.scrollWidth > bar.clientWidth) {
            gsap.to(bar, { scrollLeft: 40, duration: 0.6, ease: 'power2.out', delay: 1.5 })
            gsap.to(bar, { scrollLeft: 0, duration: 0.5, ease: 'power2.inOut', delay: 2.3 })
        }
    })

    // Sliding indicator
    const indicator = document.createElement('div')
    indicator.className = 'preset-indicator'
    bar.appendChild(indicator)

    const pills = []
    const currentPresetName = detectCurrentPreset()
    activePresetName = currentPresetName

    Object.entries(builtinPresets).forEach(([name]) => {
        const pill = document.createElement('button')
        pill.className = 'preset-pill'
        pill.textContent = name
        pill.dataset.preset = name
        if (name === currentPresetName) pill.classList.add('active')

        // GSAP hover
        pill.addEventListener('mouseenter', () => {
            if (pill.classList.contains('active')) return
            gsap.to(pill, { color: 'rgba(255,255,255,0.7)', duration: 0.4, ease: 'power3.out' })
        })
        pill.addEventListener('mouseleave', () => {
            if (pill.classList.contains('active')) return
            gsap.to(pill, { color: 'rgba(255,255,255,0.35)', duration: 0.6, ease: 'power2.out' })
        })

        pill.addEventListener('click', () => switchPreset(name, bar, rotateToggle, pills, indicator))
        bar.appendChild(pill)
        pills.push(pill)
    })

    // Position indicator on active pill after layout + scroll into view
    requestAnimationFrame(() => {
        const activePill = bar.querySelector('.preset-pill.active')
        if (activePill) {
            positionIndicator(indicator, activePill, bar, false)
            const barRect = bar.getBoundingClientRect()
            const pillRect = activePill.getBoundingClientRect()
            const scrollTo = activePill.offsetLeft - (barRect.width / 2) + (pillRect.width / 2)
            bar.scrollLeft = Math.max(0, scrollTo)
            updateFadeEdges()
        }
    })

    // --- Orbit hint ---
    const hint = document.createElement('div')
    hint.className = 'orbit-hint'
    hint.innerHTML = `
        <svg class="orbit-hint-icon" viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round">
            <circle cx="20" cy="20" r="14" stroke-dasharray="4 3" opacity="0.5"/>
            <path d="M20 16v8m-4-4h8"/>
        </svg>
        <span>drag and zoom me</span>
    `
    document.body.appendChild(hint)

    const dismissHint = () => {
        gsap.to(hint, { opacity: 0, duration: 0.6, ease: 'power2.out', onComplete: () => hint.remove() })
        canvas.removeEventListener('pointerdown', dismissHint)
        canvas.removeEventListener('wheel', dismissHint)
    }
    const canvas = document.querySelector('canvas.viewport')
    canvas.addEventListener('pointerdown', dismissHint, { once: true })
    canvas.addEventListener('wheel', dismissHint, { once: true })

    // Auto-dismiss after 5s
    setTimeout(() => { if (hint.parentNode) dismissHint() }, 5000)

    // Show hint after particles fade in
    gsap.fromTo(hint,
        { opacity: 0, y: 6 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out', delay: 2.5 }
    )

    // Initial fade in
    window._animateFade(1.0)
}

const positionIndicator = (indicator, pill, bar, animate = true) => {
    const x = pill.offsetLeft
    const w = pill.offsetWidth
    const h = pill.offsetHeight

    if (animate) {
        gsap.to(indicator, {
            x, width: w, height: h,
            duration: 0.55,
            ease: 'power2.out',
        })
    } else {
        gsap.set(indicator, { x, width: w, height: h })
    }
}

const detectCurrentPreset = () => {
    const keys = ['shape', 'plyFile', 'imageFile', 'particleCount', 'shapeText', 'shapeScale', 'particleColor', 'backgroundColor']
    for (const [name, preset] of Object.entries(builtinPresets)) {
        const match = keys.every(k => preset[k] === undefined || preset[k] === params[k])
        if (match) return name
    }
    return Object.keys(builtinPresets)[0] || null
}

const toggleInfoPopup = (show) => {
    const overlay = document.querySelector('.info-overlay')
    if (!overlay) return
    overlay.classList.toggle('visible', show)
}

let transitioning = false
let activePresetName = null

const switchPreset = async (name, bar, rotateToggle, pills, indicator) => {
    const preset = builtinPresets[name]
    if (!preset || transitioning || name === activePresetName) return

    transitioning = true

    try {
        // Slide indicator to new pill
        const targetPill = pills.find(p => p.dataset.preset === name)

        // Update active class
        pills.forEach(p => {
            if (p.classList.contains('active')) {
                p.classList.remove('active')
                gsap.to(p, { color: 'rgba(255,255,255,0.35)', duration: 0.4, ease: 'power2.out' })
            }
        })
        if (targetPill) {
            targetPill.classList.add('active')
            gsap.to(targetPill, { color: 'rgba(255,255,255,0.9)', duration: 0.4, ease: 'power2.out' })
            positionIndicator(indicator, targetPill, bar, true)
        }
        activePresetName = name

        const needsRebuild = (preset.particleCount && preset.particleCount !== params.particleCount)
            || (preset.shape && preset.shape !== params.shape)
            || (preset.shapeText && preset.shapeText !== params.shapeText)
            || (preset.shapeScale && preset.shapeScale !== params.shapeScale)
            || (preset.morphTarget && preset.morphTarget !== params.morphTarget)
            || (preset.plyFile && preset.plyFile !== params.plyFile)
            || (preset.imageFile && preset.imageFile !== params.imageFile)

        // Fast fade out
        await window._animateFade(0.0, 'out')

        // Apply new params
        Object.assign(params, defaultParams, preset)
        saveParams()

        if (needsRebuild) {
            await window._rebuildScene()
        }

        // Apply all uniforms
        window._applyPresetParams()

        // Update rotate toggle state
        if (rotateToggle) rotateToggle.classList.toggle('active', params.autoRotate)

        // Fade in — fire and forget, don't block next switch
        window._animateFade(1.0, 'in')
    } finally {
        transitioning = false
    }
}
